import React, { CSSProperties, useLayoutEffect, useRef } from 'react';
import { capitalizeFirstLetter, IDays, IProductionCapacitySlot, translateWeekDay } from '@app/common';
import {
  CapacityMetric,
  CapacitySection,
  formatHHMM,
  formatOpenHours,
  getSlotLimit,
  OpenStarts,
  slotKey,
  SlotKey,
  WEEK_DAYS,
} from './production-capacity-editor.helpers';
import { clickAway, colors, fontFamily, hatch } from './editor.styles';
import { CopyIcon, MoreIcon, SelectAllIcon, TrashIcon } from './icons';

const SECTION_HEIGHT = 28;
const SLOT_HEIGHT = 24;
const ROW_GAP = 3;
const HEADER_HEIGHT = 80;

const GRID_COLUMNS = '72px repeat(7, minmax(120px, 1fr))';

export const dayLabel = (day: IDays) => capitalizeFirstLetter(translateWeekDay(day));

interface CapacityGridProps {
  sections: CapacitySection[];
  openStarts: OpenStarts;
  slotsByDay: Record<IDays, Map<number, IProductionCapacitySlot>>;
  metric: CapacityMetric;
  selected: Set<SlotKey>;
  copySource: IDays | null;
  menuDay: IDays | null;
  onToggleKeys: (keys: SlotKey[]) => void;
  onToggleMenu: (day: IDays) => void;
  onCopyDay: (day: IDays) => void;
  onSelectDay: (day: IDays) => void;
  onClearDay: (day: IDays) => void;
}

const cellStyle = (value: number | undefined, max: number, isSelected: boolean): CSSProperties => {
  const base: CSSProperties = {
    height: SLOT_HEIGHT,
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 5,
    padding: 0,
    fontFamily,
    fontSize: 12,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    cursor: 'pointer',
    boxShadow: isSelected ? `inset 0 0 0 2px ${colors.selection}` : undefined,
  };

  if (value === undefined) {
    return { ...base, background: 'transparent', border: '1px dashed rgba(253,255,252,0.18)', color: colors.subtle };
  }

  if (value === 0) {
    return { ...base, background: 'rgba(220,0,0,0.22)', border: '1px solid rgba(255,120,120,0.35)', color: colors.dangerText };
  }

  const alpha = 0.14 + 0.5 * Math.min(1, value / max);

  return { ...base, background: `rgba(0,160,214,${alpha.toFixed(2)})`, border: '1px solid transparent', color: colors.text };
};

const menuItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minHeight: 40,
  padding: '0 10px',
  borderRadius: 7,
  border: 'none',
  background: 'transparent',
  color: colors.text,
  fontFamily,
  fontSize: 14,
  fontWeight: 500,
  textAlign: 'left',
  cursor: 'pointer',
};

const hiddenButton: CSSProperties = {
  background: 'none',
  border: 'none',
  fontFamily,
  cursor: 'pointer',
};

const columnBorder = (isSource: boolean) => `1px solid ${isSource ? colors.selection : 'rgba(253,255,252,0.06)'}`;

export const CapacityGrid = ({
  sections,
  openStarts,
  slotsByDay,
  metric,
  selected,
  copySource,
  menuDay,
  onToggleKeys,
  onToggleMenu,
  onCopyDay,
  onSelectDay,
  onClearDay,
}: CapacityGridProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const headerGridRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // L'en-tête des jours est hors de la zone qui défile : on lui réserve la largeur de la barre de défilement
  // et on le décale avec le défilement horizontal
  useLayoutEffect(() => {
    const body = bodyRef.current;

    if (!body) return;

    const syncScrollbar = () => {
      if (headerRef.current) {
        headerRef.current.style.paddingRight = `${body.offsetWidth - body.clientWidth}px`;
      }
    };

    syncScrollbar();

    const observer = new ResizeObserver(syncScrollbar);
    observer.observe(body);

    return () => observer.disconnect();
  }, []);

  const onBodyScroll = () => {
    if (headerGridRef.current && bodyRef.current) {
      headerGridRef.current.style.transform = `translateX(${-bodyRef.current.scrollLeft}px)`;
    }
  };

  const limitOf = (day: IDays, start: number) => getSlotLimit(slotsByDay[day].get(start), metric.key);

  const max = Math.max(
    1,
    ...WEEK_DAYS.flatMap((day) => [...openStarts[day]].map((start) => limitOf(day, start) ?? 0)),
  );

  const rowsHeight = sections.reduce(
    (height, section) => height + SECTION_HEIGHT + section.starts.length * (SLOT_HEIGHT + ROW_GAP) + ROW_GAP,
    -ROW_GAP,
  );

  const sectionKeys = (section: CapacitySection) => WEEK_DAYS.flatMap((day) => (
    section.starts.filter((start) => openStarts[day].has(start)).map((start) => slotKey(day, start))
  ));

  const rowKeys = (start: number) => WEEK_DAYS
    .filter((day) => openStarts[day].has(start))
    .map((day) => slotKey(day, start));

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', fontFamily, color: colors.text }}>
      {/* overflowX clip (et non hidden) pour laisser déborder le menu ⋯ vers le bas */}
      <div ref={headerRef} style={{ position: 'relative', zIndex: 2, flexShrink: 0, overflowX: 'clip', overflowY: 'visible' }}>
        <div ref={headerGridRef} style={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, columnGap: 8 }}>
          <div />

          {WEEK_DAYS.map((day) => {
            const open = openStarts[day];
            const label = dayLabel(day);
            const isClosed = open.size === 0;
            const isMenuOpen = menuDay === day;

            const limits = [...open].map((start) => limitOf(day, start));
            const unlimited = limits.filter((limit) => limit === undefined).length;
            const total = limits.reduce<number>((sum, limit) => sum + (limit ?? 0), 0);
            const totalLabel = unlimited === open.size ? 'Sans limite' : `${total} ${metric.unit}`;
            const unlimitedLabel = unlimited > 0 && unlimited < open.size ? `+${unlimited} sans limite` : '';

            return (
              <div
                key={day}
                style={{ position: 'relative', zIndex: isMenuOpen ? 3 : 1, height: HEADER_HEIGHT, boxSizing: 'border-box', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3, borderRadius: '12px 12px 0 0', background: colors.column, border: columnBorder(copySource === day), borderBottom: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, height: 26 }}>
                  <button
                    type="button"
                    disabled={isClosed}
                    aria-label={`Sélectionner tous les créneaux du ${label.toLowerCase()}`}
                    onClick={() => onSelectDay(day)}
                    style={{ ...hiddenButton, padding: 0, color: colors.text, fontSize: 15, fontWeight: 700, textAlign: 'left', cursor: isClosed ? 'default' : 'pointer' }}>
                    {label}
                  </button>

                  {!isClosed && (
                    <div style={{ position: 'relative' }}>
                      {isMenuOpen && <div style={clickAway} onClick={() => onToggleMenu(day)} />}

                      <button
                        type="button"
                        aria-label={`Actions pour le ${label.toLowerCase()}`}
                        aria-haspopup="menu"
                        aria-expanded={isMenuOpen}
                        onClick={() => onToggleMenu(day)}
                        style={{ position: 'relative', zIndex: 10, width: 30, height: 26, padding: 0, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', ...(isMenuOpen ? { background: 'rgba(253,255,252,0.12)', border: '1px solid rgba(253,255,252,0.24)', color: colors.text } : { background: 'transparent', border: '1px solid transparent', color: colors.soft }) }}>
                        <MoreIcon />
                      </button>

                      {isMenuOpen && (
                        <div
                          role="menu"
                          aria-label={`Actions pour le ${label.toLowerCase()}`}
                          onKeyDown={(event) => event.key === 'Escape' && onToggleMenu(day)}
                          style={{ position: 'absolute', top: 32, right: 0, zIndex: 10, width: 260, boxSizing: 'border-box', padding: 6, background: colors.raised, border: '1px solid rgba(253,255,252,0.14)', borderRadius: 10, boxShadow: '0 16px 40px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <button type="button" role="menuitem" onClick={() => onCopyDay(day)} style={menuItem}>
                            <CopyIcon /> Copier vers d'autres jours…
                          </button>
                          <button type="button" role="menuitem" onClick={() => onSelectDay(day)} style={menuItem}>
                            <SelectAllIcon /> Sélectionner la journée
                          </button>
                          <div style={{ height: 1, margin: '4px 6px', background: 'rgba(253,255,252,0.10)' }} />
                          <button type="button" role="menuitem" onClick={() => onClearDay(day)} style={{ ...menuItem, color: colors.dangerText }}>
                            <TrashIcon /> Retirer les limites {metric.label.toLowerCase()}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 11, fontWeight: 500, lineHeight: '14px', color: colors.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {isClosed ? 'Fermé' : formatOpenHours(open)}
                </div>

                {!isClosed && (
                  <div title={[totalLabel, unlimitedLabel].filter(Boolean).join(' ')} style={{ fontSize: 12, lineHeight: '16px', display: 'flex', gap: 6, alignItems: 'baseline', whiteSpace: 'nowrap', minWidth: 0 }}>
                    <span style={{ fontWeight: 700, flexShrink: 0 }}>{totalLabel}</span>
                    {!!unlimitedLabel && <span style={{ color: colors.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{unlimitedLabel}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div ref={bodyRef} onScroll={onBodyScroll} style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, columnGap: 8, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: ROW_GAP, paddingTop: ROW_GAP }}>
            {sections.map((section) => (
              <React.Fragment key={section.key}>
                <button
                  type="button"
                  title={section.range}
                  aria-label={`Sélectionner tous les créneaux ${section.name.toLowerCase()} (${section.range})`}
                  onClick={() => onToggleKeys(sectionKeys(section))}
                  style={{ ...hiddenButton, height: SECTION_HEIGHT, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px 0 0', color: colors.accent, fontSize: 12, lineHeight: '14px', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                  {section.name}
                </button>

                {section.starts.map((start) => (
                  <button
                    key={start}
                    type="button"
                    aria-label={`Sélectionner le créneau de ${formatHHMM(start)} sur toute la semaine`}
                    onClick={() => onToggleKeys(rowKeys(start))}
                    style={{ ...hiddenButton, height: SLOT_HEIGHT, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 8px 0 0', color: colors.muted, fontSize: 12, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {formatHHMM(start)}
                  </button>
                ))}
              </React.Fragment>
            ))}
          </div>

          {WEEK_DAYS.map((day) => {
            const open = openStarts[day];
            const label = dayLabel(day);

            return (
              <div
                key={day}
                style={{ display: 'flex', flexDirection: 'column', gap: ROW_GAP, padding: `${ROW_GAP}px 8px 8px`, borderRadius: '0 0 12px 12px', background: colors.column, border: columnBorder(copySource === day), borderTop: 'none' }}>
                {open.size === 0 ? (
                  <div style={{ height: rowsHeight, borderRadius: 8, background: hatch, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: colors.soft }}>Fermé</span>
                    <span style={{ fontSize: 12, color: colors.muted, textAlign: 'center', maxWidth: 130 }}>D'après les horaires d'ouverture du restaurant</span>
                  </div>
                ) : sections.map((section) => (
                  <React.Fragment key={section.key}>
                    <div style={{ height: SECTION_HEIGHT, display: 'flex', alignItems: 'center' }}>
                      <div style={{ height: 1, width: '100%', background: 'rgba(253,255,252,0.10)' }} />
                    </div>

                    {section.starts.map((start) => {
                      if (!open.has(start)) {
                        return <div key={start} style={{ height: SLOT_HEIGHT, borderRadius: 5, background: hatch }} />;
                      }

                      const key = slotKey(day, start);
                      const value = limitOf(day, start);
                      const isSelected = selected.has(key);

                      return (
                        <button
                          key={start}
                          type="button"
                          aria-pressed={isSelected}
                          aria-label={`${label} ${formatHHMM(start)} : ${value === undefined ? 'sans limite' : value === 0 ? 'bloqué' : `${value} ${metric.unit}`}`}
                          onClick={() => onToggleKeys([key])}
                          style={cellStyle(value, max, isSelected)}>
                          {value === undefined ? '–' : value}
                        </button>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
